import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  groupPermissionsService,
  type CreateGroupPermissionData,
} from "../../services/admin/grouppermissions.service";

export function useGroupPermissions() {
  return useQuery({
    queryKey: ["admin", "group_permissions"],
    queryFn: groupPermissionsService.getAll,
  });
}

export function useGroupPermission(id: string) {
  return useQuery({
    queryKey: ["admin", "group_permissions", id],
    queryFn: () => groupPermissionsService.getById(id),
    enabled: !!id,
  });
}

export function useGroupPermissionsByGroupId(groupId: string) {
  return useQuery({
    queryKey: ["admin", "group_permissions", "by_group", groupId],
    queryFn: () => groupPermissionsService.getByGroupId(groupId),
    enabled: !!groupId,
  });
}

export function useCreateGroupPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGroupPermissionData) =>
      groupPermissionsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "group_permissions"],
      });
      notifications.show({
        title: "Success",
        message: "Permission added to group successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add permission to group",
        color: "red",
      });
    },
  });
}

export function useDeleteGroupPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => groupPermissionsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "group_permissions"],
      });
      notifications.show({
        title: "Success",
        message: "Permission removed from group successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to remove permission from group",
        color: "red",
      });
    },
  });
}
